const SAME_TAB_EVENT = 'aktify-local-table-change';

interface SameTabEventDetail {
  key: string;
}

export const readTable = <T>(key: string): T[] => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch (e) {
    console.error('local-table read failed for', key, e);
    return [];
  }
};

export const writeTable = <T>(key: string, rows: T[]): void => {
  try {
    localStorage.setItem(key, JSON.stringify(rows));
    window.dispatchEvent(new CustomEvent<SameTabEventDetail>(SAME_TAB_EVENT, { detail: { key } }));
  } catch (e) {
    console.error('local-table write failed for', key, e);
  }
};

/**
 * Subscribe to writes against any localStorage key (same-tab writes via our
 * CustomEvent, other tabs via the native `storage` event). The callback receives
 * no payload — call your own reader for the latest value. Use this for non-array
 * shapes (objects, maps, scalars).
 */
export const subscribeKey = (key: string, cb: () => void): (() => void) => {
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== key) return;
    cb();
  };

  const handleSameTab = (event: Event) => {
    const detail = (event as CustomEvent<SameTabEventDetail>).detail;
    if (!detail || detail.key !== key) return;
    cb();
  };

  window.addEventListener('storage', handleStorage);
  window.addEventListener(SAME_TAB_EVENT, handleSameTab);

  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(SAME_TAB_EVENT, handleSameTab);
  };
};

/**
 * Subscribe to writes against a localStorage array key; the callback receives the
 * freshly-read rows. Thin wrapper over `subscribeKey`.
 */
export const subscribeTable = <T>(key: string, cb: (rows: T[]) => void): (() => void) =>
  subscribeKey(key, () => cb(readTable<T>(key)));
